import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Dropdown, DropdownButton } from "react-bootstrap";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import SettingsTabs from "../AllTabs/SettingsTabs";
import { useAuth } from "../../Common/Configurations/AuthContext";
import moment from "moment";
import { toast } from "react-toastify";
import DefaultData from "../../CompanyData/DefaultData";

const CreditHistory = () => {
  const { user, loading, refreshUserProfile } = useAuth();
  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [data, setData] = useState({});
  const [pricingPlans, setPricingPlans] = useState([]);
  const [isRecharging, setIsRecharging] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);

  const GetData = async (pageNum) => {
    if (!user) return;
    setIsLoading(true);
    try {
      const response = await apiService.get(
        `${config.GETCREDITHISTORY}/${user.cid}`,
        pageSize,
        pageNum
      );

      setLists(response.data || []); // Use 'lists' state
      setPageNumber(response.pageNumber);
      setTotalPages(response.totalPages);
      setTotalRecords(response.totalRecords);
    } catch (error) {
      console.error("Error fetching credit history:", error);
      // Error toast is likely handled by apiService, but you can add more here if needed
    } finally {
      setIsLoading(false);
    }
  };

  const GetPreviousPageData = () => {
    if (pageNumber > 1) {
      GetData(pageNumber - 1);
    }
  };

  // This function goes to the NEXT page (pageNumber + 1)
  const GetNextPageData = () => {
    if (pageNumber < totalPages) {
      GetData(pageNumber + 1);
    }
  };

  // This function handles clicks on the numbered pages
  const GetPageData = (number) => {
    // The if condition from Branch.js is not needed and can cause issues.
    // It's better to just fetch the data for the page that was clicked.
    GetData(number);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }
  const renderPageNumbers = pageNumbers.map((number) => {
    const isActive = number === pageNumber ? "active" : "";
    return (
      <li
        className={`page-item ${isActive}`}
        key={number}
        onClick={() => GetPageData(number)}
      >
        <Link className="page-link">{number}</Link>
      </li>
    );
  });

  // fetch recharge pricing plans
  const getPricingPlans = async () => {
    try {
      const plans = await apiService.getAll(config.GETPRICINGPLANS);
      setPricingPlans(plans || []);
    } catch (error) {
      console.error("Error fetching pricing plans:", error);
    }
  };

  // fetch company data for balance
  const getCompanyData = async () => {
    if (!user) return;
    try {
      const companyData = await apiService.getById(
        config.GETCOMPANYBYID,
        user.cid
      );
      setCurrentBalance(companyData.connects);
    } catch (error) {
      console.error("Could not fetch latest company balance", error);
    }
  };

  useEffect(() => {
    if (user && !loading) {
      getCompanyData();
      GetData(1); // Always fetch the first page on initial load
      getPricingPlans();
    }
  }, [user, loading]);

  // Razorpay integration
  function loadScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  async function displayRazorpay(plan) {
    const res = await loadScript(
      "https://checkout.razorpay.com/v1/checkout.js"
    );

    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    const result = await apiService.post(
      `${config.MAKEPAYMENT}?Amount=${plan.price}&PayRequest=RAZORPAY&CompanyId=${user?.cid}`
    );

    if (!result.succeeded) {
      toast.error("Payment initiation failed. Try again.");
      return;
    }

    const { amount, id: order_id, currency, razorpayKey, name } = result.data;

    const options = {
      key: razorpayKey,
      amount: amount,
      currency: currency,
      name: name,
      description: `Recharge ${plan.connects} connects`,
      order_id: order_id,
      handler: async function (response) {
        const payload = {
          companyId: user.cid,
          connects: plan.connects,
          purpose: `Recharge: ${plan.connects} connects for ₹${plan.price}`,
          transactionNo: response.razorpay_payment_id,
        };

        try {
          await apiService.post(config.RECHARGECONNECTS, payload);
          await refreshUserProfile();
          toast.success("Recharge successful!");
          GetData(pageNumber);
          getCompanyData();
        } catch (error) {}
      },
      prefill: {
        email: user?.email,
        contact: user?.phoneNumber,
      },
      notes: {
        address: "Smart Travel Recharge",
      },
      theme: {
        color: "#3399cc",
      },
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.open();
  }

  // updated recharge handler
  const handleRecharge = async (plan) => {
    if (!user) return;
    displayRazorpay(plan);
  };

  if (loading || !user) {
    return <p>Loading...</p>;
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <SettingsTabs pageName="CREDITHISTORY" />
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 className="card-title">Connects Transaction History</h4>
                    <p className="text-muted mb-0">
                      Your current balance is:{" "}
                      <strong>{currentBalance} Connects</strong>
                    </p>
                  </div>
                    <div>
                      <DropdownButton
                        id="recharge-dropdown"
                        title={
                          isRecharging ? "Processing..." : "Recharge Connects"
                        }
                        disabled={isRecharging}
                      >
                        {pricingPlans.length > 0 ? (
                          pricingPlans.map((plan) => (
                            <Dropdown.Item
                              key={plan.connects}
                              onClick={() => handleRecharge(plan)}
                            >
                              {plan.connects} Connects - INR {plan.price}
                            </Dropdown.Item>
                          ))
                        ) : (
                          <Dropdown.Item disabled>
                            Loading plans...
                          </Dropdown.Item>
                        )}
                      </DropdownButton>
                    </div>
                </div>

                {isLoading ? (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Purpose</th>
                          <th className="text-end">Connects</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lists.length > 0 ? (
                          lists.map((item, index) => (
                            <tr key={item.creditHistoryId}>
                              <td>{index + 1}</td>
                              {/* <td>{moment(item.createdDate).format('DD MMM YYYY, h:mm A')}</td> */}
                              <td>
                                {moment
                                  .utc(item.createdDate)
                                  .local()
                                  .format("DD MMM YYYY, hh:mm A")}
                              </td>
                              <td>
                                {item.type === "Credit" ? (
                                  <span className="badge bg-success-subtle text-success">
                                    Credit
                                  </span>
                                ) : (
                                  <span className="badge bg-danger-subtle text-danger">
                                    Debit
                                  </span>
                                )}
                              </td>
                              <td>{item.purpose}</td>
                              <td
                                className={`text-end fw-bold ${
                                  item.type === "Credit"
                                    ? "text-success"
                                    : "text-danger"
                                }`}
                              >
                                {item.type === "Credit"
                                  ? `+${item.connects}`
                                  : `-${item.connects}`}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              No transaction history found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                  <div className="flex-shrink-0">
                    <div className="text-muted">
                      Showing{" "}
                      <span className="fw-semibold">{lists.length}</span> of{" "}
                      <span className="fw-semibold">{totalRecords}</span>{" "}
                      Results
                    </div>
                  </div>
                  <ul className="pagination pagination-separated pagination-sm mb-0">
                    <li
                      className={`page-item ${
                        pageNumber <= 1 ? "disabled" : ""
                      }`}
                      onClick={GetPreviousPageData}
                    >
                      <Link className="page-link">Previous</Link>
                    </li>

                    {renderPageNumbers}

                    <li
                      className={`page-item ${
                        pageNumber >= totalPages ? "disabled" : ""
                      }`}
                      onClick={GetNextPageData}
                    >
                      <Link className="page-link">Next</Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditHistory;
